import { useCallback, useRef } from "react";

import { isApolloError } from "@apollo/client";
import { useNavigate } from "react-router";

import { RESTOCKING_PROGRESSED_METHOD } from "analytics/events";
import { routes } from "config/routes";
import { useBarcodeScanner } from "shared/hooks/useBarcodeScanner";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useScan } from "shared/hooks/useScan/useScan";
import { IntlMessageId } from "shared/lang";
import { isShelfNumberFormat } from "utils/scan";

import { RestockingListPreparationList } from "../../components/RestockingListPreparationList";
import { useResolveEANToSkus } from "../../hooks/useResolveEanToSkus";
import { RestockingItem } from "../../models/restockingItem/types";
import { usePrivateRestockingList } from "../../services/restockingService/service";
import { useRestockingProductSearchStore } from "../../stores/restockingProductSearchStore";

const ADD_UNIT_TO_THE_LIST_ERROR_TOAST_ID = "add-unit-to-the-list-error-toast-id";

export function RestockingListPreparation() {
  const { showToastUI } = useCustomToast();

  const navigate = useNavigate();
  const scrollContainer = useRef<HTMLDivElement | null>(null);
  const resolveEANToSkus = useResolveEANToSkus();
  const { processBarcode } = useBarcodeScanner();

  const {
    privateRestockingListData,
    isLoadingPrivateRestockingList,
    addItemsToPrivateRestockingList,
  } = usePrivateRestockingList();

  const setInitialSearchQuery = useRestockingProductSearchStore(
    (state) => state.setInitialSearchQuery,
  );

  const showErrorToast = useCallback(
    (errorMessage: IntlMessageId) => {
      showToastUI({
        id: ADD_UNIT_TO_THE_LIST_ERROR_TOAST_ID,
        containerStyle: {
          width: "100%",
          bottom: "150px",
          position: "absolute",
          paddingLeft: "s100",
          paddingRight: "s100",
        },
        title: errorMessage,
      });
    },
    [showToastUI],
  );

  const addItemsToPrivateRestockingListByScanning = useCallback(
    async (ean: string) => {
      const skus = await resolveEANToSkus(ean);
      if (skus.length === 0) {
        showErrorToast(
          "flows.inventory.restocking-list.flows.inventory.restocking-list.unassigned-error",
        );
        return;
      }
      if (skus.length > 1) {
        setInitialSearchQuery(ean);
        navigate(routes.inventory.restockingList.textSearch);
        return;
      }
      const [sku] = skus;
      const isSkuAlreadyInPrivateList = privateRestockingListData?.restockingItems.some(
        (item: RestockingItem) => sku === item.sku,
      );
      if (isSkuAlreadyInPrivateList) {
        showErrorToast("flows.inventory.restocking-list.item-is-already-in-the-list");
        return;
      }
      try {
        await addItemsToPrivateRestockingList([sku], RESTOCKING_PROGRESSED_METHOD.SCANNED_PRODUCT);
      } catch (error: any) {
        const isLimitExceeded =
          error &&
          isApolloError(error) &&
          error.graphQLErrors?.[0]?.extensions?.code ===
            "PRIVATE_RESTOCKING_LIST_LIMIT_EXCEEDED_ERROR";

        if (isLimitExceeded) {
          showToastUI({
            id: "create-private-restocking-error",
            title: "create-private-restocking-error.item-limit-exceeded",
          });
          return;
        }

        showErrorToast("flows.inventory.restocking-list.add-items-to-private-list-error");
      }
    },
    [
      resolveEANToSkus,
      privateRestockingListData?.restockingItems,
      showErrorToast,
      setInitialSearchQuery,
      navigate,
      addItemsToPrivateRestockingList,
      showToastUI,
    ],
  );

  const onScan = useCallback(
    (value: string) => {
      if (isShelfNumberFormat(value)) {
        setInitialSearchQuery(value);
        navigate(routes.inventory.restockingList.shelfSearch);
        return;
      }
      const searchedEan = processBarcode(value);
      if (searchedEan) {
        addItemsToPrivateRestockingListByScanning(searchedEan);
      }
    },
    [setInitialSearchQuery, navigate, addItemsToPrivateRestockingListByScanning, processBarcode],
  );

  useScan({ onScan });

  return (
    <RestockingListPreparationList
      isLoading={isLoadingPrivateRestockingList}
      scrollContainer={scrollContainer}
    />
  );
}
